import React, { useEffect, useRef } from 'react';
import useReveal from '../useReveal';
import { trackEvent } from '../../../utils/analytics';
import { WALL_HEAD, WALL_PANES } from './wallContent';
import './AIEFeatureBlocks.css';
import './AIEWall.css';

/**
 * AIEWall — "What they can do" as the sticky-split wall (Nitish-ruled Aug 31 -
 * Sep 1 2026, ~45 live rulings; replaces the nine stacked AIEFeatureBlocks).
 *
 * Layout: left column = nine pane copies that scroll; right column = ONE
 * pinned frame whose story switches to the active pane, with the section's
 * single CTA fixed beneath it (his placement: anchored to the proof, on
 * screen for the whole scroll). On phones nothing pins: panes render their
 * frames inline, and the page's own mobile sticky bar carries the action
 * (s238, his ruling: no in-pane phone CTA — it duplicated the sticky bar).
 *
 * Frame markup is GENERATED — wallContent.js is emitted by
 * work/aie_wall/port_wall.py (Maeve's workspace) from the approved mock
 * (aie_wall_mocks/w.html). Never hand-edit wallContent.js; regenerate it.
 * Pane headings/subs render as real text (crawlable), and the frame HTML is
 * injected before prerender, so the whole wall ships as static HTML — the
 * round-6 law (fold prerendered, never JS-mounted) extended to the wall.
 *
 * Reuse (Sep 23 2026, role-page experiment): the role pages render this SAME
 * component with their own `head`, `panes`, `page` and `sectionId`. Defaults
 * are the /ai-employees values, so that page renders exactly as before.
 *
 * The scroll driver toggles .is-on by whichever pane's center is nearest the
 * viewport center, and restarts the newly active frame's story from t=0 so a
 * first-time viewer never lands mid-cycle (his s232 fix). ReactSnap-safe: at
 * prerender scrollY=0, apply() selects pane 0, matching the static markup.
 */
/** "03 · DRIVE" -> "drive", "07 · COWORK & BROWSE" -> "cowork-browse": the
 *  pane's name without its position, for analytics that survive a reorder. */
const paneName = (pane) => (pane && pane.num
  ? pane.num.replace(/^\s*\d+\s*\u00b7\s*/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  : null);

const AIEWall = ({
  ctaLabel,
  onPrimarySticky,
  head = WALL_HEAD,
  panes = WALL_PANES,
  page = 'ai-employees',
  sectionId = 'aie-how',
}) => {
  const rootRef = useRef(null);
  const panesRef = useRef(panes);
  panesRef.current = panes;
  const pageRef = useRef(page);
  pageRef.current = page;
  const [headRef, headVisible] = useReveal();

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const paneEls = Array.from(root.querySelectorAll('.w-pane'));
    const slides = Array.from(root.querySelectorAll('.w-slide'));
    if (!paneEls.length) return undefined;
    window.__wallDriver = 'io-raf-v3'; // build marker (QC: proves which driver is live)
    let cur = -1;
    // Restart the active frame's story from its beginning (Nitish, Aug 31).
    // Also fired when the WALL itself enters the viewport (Sep 9): pane 1 is
    // the active pane from page load, so a scroll (or the fold's "See how it
    // works") that arrives at it never changed the active pane, and the
    // story was deep into the chat screen instead of the typed prompt.
    const restartActive = () => {
      const act = slides[cur < 0 ? 0 : cur];
      if (act && act.getAnimations) {
        act.getAnimations({ subtree: true }).forEach((a) => {
          try { a.currentTime = 0; a.play(); } catch (e) { /* noop */ }
        });
      }
    };
    const apply = () => {
      const mid = window.innerHeight / 2;
      let best = 0;
      let bestD = Infinity;
      paneEls.forEach((p, i) => {
        const r = p.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - mid);
        if (d < bestD) { bestD = d; best = i; }
      });
      if (best === cur) return;
      cur = best;
      paneEls.forEach((p, i) => {
        p.classList.toggle('is-on', i === cur);
        Array.from(p.querySelectorAll('.w-rail i')).forEach((t, j) => {
          t.classList.toggle('is-on', j <= cur);
        });
      });
      slides.forEach((s, i) => {
        s.classList.toggle('is-on', i === cur);
        // Inactive slides are opacity:0 (still in the accessibility tree): hide them from
        // screen readers so only the visible frame is read (Rhea's gate-3 rule, Sep 23 2026).
        s.setAttribute('aria-hidden', i === cur ? 'false' : 'true');
      });
      restartActive();
      window.__wallActive = cur; // QC hook (same contract as the mock's __wActive)
    };
    // Mobile (inline frames): restart a frame's story the moment it scrolls
    // into view (s238, his ask — the desktop pane-switch restart principle
    // applied to phones: nobody catches an animation mid-cycle). Fires on
    // every entry, so scrolling back up also restarts. Desktop unaffected:
    // .w-inline is display:none >900px and never intersects.
    const inlines = Array.from(root.querySelectorAll('.w-inline'));
    let ioInline;
    if (typeof IntersectionObserver !== 'undefined' && inlines.length) {
      ioInline = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting || !en.target.getAnimations) return;
          en.target.getAnimations({ subtree: true }).forEach((a) => {
            try { a.currentTime = 0; a.play(); } catch (e) { /* noop */ }
          });
        });
      }, { threshold: 0.25 });
      inlines.forEach((el) => ioInline.observe(el));
    }
    // Driver: an IntersectionObserver gates a rAF loop that re-evaluates the
    // active pane every frame while the wall is on screen. No scroll listener
    // at all — the page scrolls inside a nested container (not the window),
    // and event plumbing proved fragile there; getBoundingClientRect is
    // viewport-relative, so polling the geometry is correct in ANY scroll
    // environment. Cost: 9 rect reads/frame, only while the wall is visible;
    // apply() exits after one comparison when nothing changed.
    let running = false;
    let raf = 0;
    const loop = () => { apply(); raf = window.requestAnimationFrame(loop); };
    let io;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => {
        const on = entries.some((en) => en.isIntersecting);
        if (on && !running) {
          running = true;
          raf = window.requestAnimationFrame(loop);
          restartActive(); // wall entered the viewport: start the story from t=0 (Sep 9)
        }
        if (!on && running) { running = false; window.cancelAnimationFrame(raf); }
      });
      io.observe(root);
    } else {
      running = true;
      raf = window.requestAnimationFrame(loop);
    }
    apply();
    // Pane depth (Nitish, Sep 16 2026): the page-level section_view sees the
    // wall as ONE section, so nothing recorded progress through its panes.
    // One section_view per pane, the first time that pane's copy is half on
    // screen (or half a viewport of it, for tall inline frames on phones):
    // section 'wall_pane', label = the pane number ('01'..'10'), pane = 1-based
    // index. Desktop and phone share this observer (the left column renders on
    // both); the driver above is untouched.
    let ioPane;
    if (typeof IntersectionObserver !== 'undefined') {
      const seenPane = new Set();
      ioPane = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const vh = (en.rootBounds && en.rootBounds.height) || window.innerHeight;
          const enough = en.intersectionRatio >= 0.5
            || en.intersectionRect.height >= vh * 0.5;
          if (!enough) return;
          const idx = Number(en.target.getAttribute('data-idx'));
          if (Number.isNaN(idx) || seenPane.has(idx)) return;
          seenPane.add(idx);
          const pane = panesRef.current[idx];
          trackEvent('section_view', {
            section: 'wall_pane',
            label: pane ? pane.num : String(idx + 1),
            pane: idx + 1,
            // Stable across reorders (Oct 12 2026): label/pane are positions,
            // pane_name is the pane itself ("drive", "spend"). Read by name.
            pane_name: paneName(pane),
            page: pageRef.current,
          });
          ioPane.unobserve(en.target);
        });
      }, { threshold: [0.1, 0.25, 0.5] });
      paneEls.forEach((p) => ioPane.observe(p));
    }
    return () => {
      if (io) io.disconnect();
      if (ioInline) ioInline.disconnect();
      if (ioPane) ioPane.disconnect();
      window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="mk-wrap w-wall" id={sectionId} ref={rootRef}>
      <div
        className={`mk-head aie-reveal ${headVisible ? 'is-visible' : ''}`}
        ref={headRef}
      >
        <span className="aie-eyebrow">{head.eyebrow}</span>
        <h2 className="aie-section-title aie-display">{head.title}</h2>
        <p className="aie-section-sub">{head.sub}</p>
      </div>
      <div className="w-scroll">
        <div className="w-left">
          {panes.map((pane, i) => (
            <div className={`w-pane${i === 0 ? ' is-on' : ''}`} data-idx={i} key={pane.num}>
              <div className="w-rail" aria-hidden="true">
                {panes.map((p2, j) => (
                  <i key={p2.num} className={i === 0 && j === 0 ? 'is-on' : undefined} />
                ))}
              </div>
              <span className="mk-num">{pane.num}</span>
              <h3 className="mk-title aie-display">{pane.title}</h3>
              <p className="mk-sub">{pane.sub}</p>
              <div
                className={`w-inline${pane.slideCls ? ` ${pane.slideCls}` : ''}`}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: pane.frameHtml }}
              />
            </div>
          ))}
        </div>
        <div className="w-right">
          <div className="w-stage">
            {panes.map((pane, i) => (
              <div
                key={pane.num}
                className={`w-slide${i === 0 ? ' is-on' : ''}${pane.slideCls ? ` ${pane.slideCls}` : ''}`}
                aria-hidden={i === 0 ? 'false' : 'true'}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: pane.frameHtml }}
              />
            ))}
          </div>
          <div className="w-cta-sticky">
            <button
              type="button"
              data-msc-suppress
              className="aie-btn aie-btn-primary"
              onClick={onPrimarySticky}
            >
              {ctaLabel} <span className="mk-arrow">&rarr;</span>
            </button>
            <div className="mk-cta__support">Try it free, no credit card needed</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIEWall;
